import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ArraySortPipe,
  ControlErrorDirective,
  LabelValueComponent,
  UIBlockerService
} from '@howdeniberia/core-front';
import { Subject } from 'rxjs';
import { distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { IResponseMessage } from 'src/app/core/models/api/sane-project/arpa-model';
import { CalculatorProjectService } from 'src/app/core/services/api/calculator/calculator-project.service';
import { BonusPipe } from 'src/app/core/services/api/shared/bonus.pipe';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { normalizeTraceHtml, toBooleanStrict } from 'src/app/shared/utils';
import { ProjectViewModel } from '../../models/project.viewmodel';
import { CalculatorMapper } from '../mappers/calculator.mapper';
import { CalculatorProductViewModel, deathsPresets } from '../models/calculator-product.viewmodel';
import { NumberMaxDirective } from '../models/number-max.directive';

@Component({
  selector: 'howden-calculator-deaths',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDividerModule,
    ArraySortPipe,
    ControlErrorDirective,
    LabelValueComponent,
    BonusPipe,
    NumberMaxDirective
  ],
  templateUrl: './calculator-deaths.component.html',
  styleUrls: ['./calculator-deaths.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalculatorDeathsComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly uiBlocker = inject(UIBlockerService);
  private readonly calcSrv = inject(CalculatorProjectService);
  private readonly sharedSrv = inject(SharedService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);

  form: FormGroup;

  readonly deathsAura = new CalculatorProductViewModel(deathsPresets['aura']);

  project!: ProjectViewModel;
  private readonly destroy$ = new Subject<void>();

  private readonly vmMap = {
    aura: this.deathsAura
  } as const;

  compareByIdBoolean = this.calcSrv.compareByIdBoolean.bind(this.calcSrv);

  public showAuraFields = false;
  canSeeTrace = false;

  constructor() {
    this.form = this.fb.group({
      deathsAura: this.deathsAura.form
    });

    this.canSeeTrace = this.sharedSrv.getCanSeeTrace();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public shouldShowZeroState(vm: CalculatorProductViewModel): boolean {
    return !vm.hasAnyPositiveNumeric();
  }

  public showTrace(): boolean {
    return !!this.canSeeTrace;
  }

  public getMessages(vm: CalculatorProductViewModel): IResponseMessage[] {
    return vm.messages ?? [];
  }

  public getTraceHtml(vm: CalculatorProductViewModel): SafeHtml | '' {
    if (!this.showTrace() || !vm.trace) { return ''; }
    const normalized = normalizeTraceHtml(vm.trace);
    return this.sanitizer.bypassSecurityTrustHtml(normalized);
  }

  public initFromViewModel(vm: ProjectViewModel['calculatorDeaths']): void {
    const model = this.sharedSrv.getModel();
    if (!model) { throw new Error('ProjectViewModel not available'); }
    this.project = model;

    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const src = vm[key];
        child.form.patchValue(src.form.getRawValue(), { emitEvent: false });

        child.bonus = src.bonus ?? 0;
        child.chargeable = !!src.chargeable;
        child.productName = src.productName ?? '';
        child.trace = src.trace ?? '';
        child.maximumCapital = src.maximumCapital ?? 0;
        child.summaryCapitals = src.summaryCapitals ?? '';
        child.telesubscriptionDirect = !!src.telesubscriptionDirect;
        child.hasTelesubscription = !!src.hasTelesubscription;
        child.telesubscriptionSingleQuestion = !!src.telesubscriptionSingleQuestion;
        child.messages = Array.isArray(src.messages) ? [...src.messages] : [];
      });

    const chargeCtrl = this.deathsAura.form.get('decAuraSelectChargeable');
    if (chargeCtrl) {
      const normalized = toBooleanStrict(chargeCtrl.value);
      if (normalized !== chargeCtrl.value) {
        chargeCtrl.patchValue(normalized, { emitEvent: false });
      }
      this.showAuraFields = normalized;
    }

    this.uiBlocker.block();
    this.calcSrv.getSChargeable()
      .pipe(finalize(() => {
        this.uiBlocker.unblock();
        this.cdr.markForCheck();
      }))
      .subscribe(list => {
        const normalizedList = (list ?? []).map((opt: any) => ({
          ...opt,
          id: toBooleanStrict(opt.id)
        }));
        this.deathsAura.setMasterValues('S_DEC_AURA_SELECT_CHARGEABLE', normalizedList);
      });

    this.setupListeners();

    this.calculateDeathsAura();
  }

  public flushToViewModel(): void {
    const vm = this.sharedSrv.getModel()!.calculatorDeaths;

    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        if (key === 'aura') {
          const ctrl = child.form.get('decAuraSelectChargeable');
          if (ctrl) {
            const normalized = toBooleanStrict(ctrl.value);
            if (normalized !== ctrl.value) {
              ctrl.patchValue(normalized, { emitEvent: false });
            }
          }
        }

        const tgt = vm[key];
        tgt.form.patchValue(child.form.getRawValue(), { emitEvent: false });

        tgt.bonus = child.bonus ?? 0;
        tgt.chargeable = !!child.chargeable;
        tgt.productName = child.productName ?? '';
        tgt.trace = child.trace ?? '';
        tgt.maximumCapital = child.maximumCapital ?? 0;
        tgt.summaryCapitals = child.summaryCapitals ?? '';
        tgt.telesubscriptionDirect = !!child.telesubscriptionDirect;
        tgt.hasTelesubscription = !!child.hasTelesubscription;
        tgt.telesubscriptionSingleQuestion = !!child.telesubscriptionSingleQuestion;
        tgt.messages = Array.isArray(child.messages) ? [...child.messages] : [];
      });
  }

  private setupListeners(): void {
    this.deathsAura.changes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calculateDeathsAura());

    const chargeCtrl = this.deathsAura.form.get('decAuraSelectChargeable');
    if (chargeCtrl) {
      chargeCtrl.valueChanges
        .pipe(
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(v => {
          const normalized = toBooleanStrict(v);
          if (normalized !== v) {
            chargeCtrl.patchValue(normalized, { emitEvent: false });
          }
          this.showAuraFields = normalized;
          this.cdr.markForCheck();
        });
    }
  }

  public calculateDeathsAura(): void {
    this.uiBlocker.block();
    CalculatorMapper.calcDecAura(
      this.project,
      this.deathsAura,
      req => this.calcSrv.resultBonusDecAura(req)
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uiBlocker.unblock();
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }
}
